(ns manage.adset-decision-tree-page.ad-variant-table-ocp.hook
  (:require [cljs-bean.core :refer [->clj]]
            [clojure.data :as data]
            [clojure.string :as str]
            [common.flags :refer [use-flag]]
            [common.queries :refer [use-query]]
            [common.resource.query.cm360-settings :as cm360.q]
            [common.util :refer [spread-obj]]
            [common.validators.core :as validators]
            [manage.adset-decision-tree-page.ad-variant-table-ocp.columns :as cols]
            [manage.adset-decision-tree-page.ad-variant-table-ocp.csv :as csv]
            [manage.adset-decision-tree-page.editor.util
             :refer [build-decision-path]]
            [manage.adset-decision-tree-page.event :as event]
            [manage.adset-decision-tree-page.sub :as dt-sub]
            [manage.adset-decision-tree-page.tagging-and-trackers.tagging.db
             :as tagging.db]
            [manage.adset.hooks :as ah]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [uix.core :as uix]
            [uix.re-frame :refer [use-subscribe]]))

(defn tagging->str [tagging]
  (when (seq tagging)
    (tagging.db/params->url (mapv (fn [{:keys [name values]}] [name (first values)]) tagging))))

(defn- variant->row-data [node-id->node-data node-id variant]
  (let [trackers (get variant :trackers [])
        click-tracker (->> trackers (filter #(= (:type %) "click")) first :value)
        impression-trackers (->> trackers (filter #(= (:type %) "impression")) (mapv :value))
        {:keys [width height]} (or (first (:ad-formats variant)) (:dimensions variant))]
    {:node-id node-id
     :decision-path (build-decision-path node-id->node-data node-id)
     :content-id (:content-id variant)
     :ad-variant-uuid (:ad-variant-uuid variant)
     :uuid (:uuid variant)
     :click-tracker click-tracker
     :impression-trackers impression-trackers
     :tagging (tagging->str (:click-through-url-tagging variant))
     :pixel (-> variant :pixels first :value)
     :cm360-metadata (:cm360-metadata variant)
     :placement-name (-> variant :cm360-metadata :placement-name)
     :dimensions (when (and width height) (str width "x" height))}))

(defn- build-rows [node-id->node-data]
  (->> node-id->node-data
       (mapcat (fn [[node-id node]]
                 (map #(variant->row-data node-id->node-data node-id %)
                      (:ad-variants node))))
       (mapv (fn [{:keys [node-id uuid content-id decision-path
                          click-tracker impression-trackers] :as row}]
               (-> row
                   (assoc :id (cols/row-id node-id uuid)
                          :adVariant content-id
                          :decisionPath decision-path
                          :clickTrackers click-tracker)
                   (into (map-indexed (fn [i v] [(keyword (str "impressionTrackers-" i)) v])
                                      impression-trackers)))))))

(defn- str->tagging [s]
  (when (seq s)
    (->> (tagging.db/url->params s)
         (keep (fn [[n v]]
                 (when-not (str/blank? n)
                   {:name n :values (cond-> [] (some? v) (conj v))})))
         vec)))

(defn get-save-data [rows impression-cols initial-rows]
  (let [id->initial-row (mc/index-by :id initial-rows)
        diff-keys (into [:clickTrackers :tagging :pixel :placement-name]
                        (map keyword impression-cols))
        changed-rows (mapv (fn [row]
                             (let [imp-trackers (seq (keep #(get row (keyword %)) impression-cols))
                                   {:keys [valid invalid]}
                                   (->> (map #(hash-map :type "impression" :value %) imp-trackers)
                                        (into [{:type "click" :value (:clickTrackers row)}])
                                        (group-by #(if (or (str/blank? (:value %))
                                                           (validators/url? (:value %)))
                                                     :valid :invalid)))]
                               (assoc row
                                      :trackers valid
                                      :impression-trackers imp-trackers
                                      :has-invalid (seq invalid))))
                           (->clj rows))
        get-rows-for-diff (fn [rs]
                            (->> rs
                                 (map #(select-keys % (conj diff-keys :id)))
                                 (mc/index-by :id)))
        [before after] (data/diff (-> initial-rows
                                      get-rows-for-diff
                                      (select-keys (map :id changed-rows)))
                                  (get-rows-for-diff changed-rows))
        changed-ids (set (concat (keys before) (keys after)))]
    {:valid (not-any? :has-invalid changed-rows)
     :bulk-patch-data
     (->> changed-rows
          (filter #(contains? changed-ids (:id %)))
          (mapv (fn [{:keys [trackers node-id uuid tagging pixel placement-name cm360-metadata]}]
                  (cond-> {:node-id node-id
                           :uuid uuid
                           :trackers (filterv #(not (str/blank? (:value %))) trackers)
                           :click-through-url-tagging (or (str->tagging tagging) [])
                           :pixels (if (str/blank? pixel) [] [{:value pixel}])}
                    (not (str/blank? placement-name))
                    (assoc :cm360-metadata (assoc cm360-metadata :placement-name placement-name)))))
          (group-by :node-id)
          (mapv (fn [[node-id variants]]
                  {:id node-id
                   :ad-variants (mapv #(select-keys % [:uuid :trackers :click-through-url-tagging
                                                       :pixels :cm360-metadata])
                                      variants)})))
     :has-overwrites
     (boolean
      (some (fn [[_id prior-vals]]
              (some (fn [[_k v]] (not (str/blank? v))) prior-vals))
            before))}))

(defn use-cm360 [initial-rows]
  (let [url-context (use-subscribe [:url-context])
        channel (ah/use-channel)
        flag-enabled? (use-flag :decision-tree-generate-external-trackers)
        available? (and flag-enabled? (= channel "meta"))
        {settings-raw :data} (use-query (spread-obj (cm360.q/cm360-settings-query url-context)
                                                    #js {:enabled available?}))
        settings (->clj settings-raw)]
    {:available available?
     :enabled (and available? (boolean (:enabled settings)))
     :settings settings
     :has-placements (boolean (some :placement-name initial-rows))}))

(defn use-rows [initial-rows]
  (let [[rows set-rows] (uix/use-state (clj->js initial-rows))
        process-row-update (uix/use-callback
                            (fn [^js new-row ^js _old-row]
                              (set-rows (fn [^js rs]
                                          (.map rs (fn [^js r]
                                                     (if (= (.-id r) (.-id new-row)) new-row r)))))
                              new-row)
                            [])
        remove-column-data (uix/use-callback
                            (fn [field]
                              (set-rows (fn [^js rs]
                                          (.map rs (fn [^js r] (js-delete r field) r)))))
                            [])]
    {:rows rows
     :set-rows set-rows
     :process-row-update process-row-update
     :remove-column-data remove-column-data}))

(defn use-columns [initial-rows remove-column-data]
  (let [[columns set-columns] (uix/use-state #(cols/initial-columns initial-rows))
        add-column (uix/use-callback
                    (fn [field]
                      (set-columns #(conj % (if (= field "impression")
                                              (str "impressionTrackers-" (random-uuid))
                                              field))))
                    [])
        delete-column (uix/use-callback
                       (fn [field]
                         (set-columns #(vec (remove #{field} %)))
                         (remove-column-data field))
                       [remove-column-data])]
    {:columns columns
     :set-columns set-columns
     :add-column add-column
     :delete-column delete-column}))

(defn use-ocp-state [{:keys [variant-row-ids read-only]}]
  (let [initial-rows (let [n->n (use-subscribe [::dt-sub/node-id->node-data])]
                       (uix/use-memo #(cond->> (build-rows n->n)
                                        (seq variant-row-ids) (filterv (comp variant-row-ids :id)))
                                     [n->n variant-row-ids]))
        {:keys [rows remove-column-data] :as row-controls} (use-rows initial-rows)
        {:keys [columns] :as column-controls} (use-columns initial-rows remove-column-data)
        cm360 (use-cm360 initial-rows)
        [show-errors set-show-errors] (uix/use-state false)
        effective-columns (if (:enabled cm360) [cols/cm360-field] columns)
        imp-columns (cols/impression-columns effective-columns)
        on-save (uix/use-callback
                 (fn []
                   (set-show-errors true)
                   (let [{:keys [valid bulk-patch-data has-overwrites]}
                         (get-save-data rows imp-columns initial-rows)
                         do-save #(rf/dispatch [::event/bulk-apply-trackers bulk-patch-data])]
                     (when valid
                       (if (seq bulk-patch-data)
                         (if has-overwrites
                           (rf/dispatch [:modal/open
                                         {:id :manage.adset-decision-tree-page.ad-variant-table-ocp.view/overwrite-warning
                                          :props {:on-save do-save}}])
                           (do-save))
                         (rf/dispatch [:ocp/close])))))
                 [rows initial-rows imp-columns])]
    {:row-controls row-controls
     :column-controls (assoc column-controls :columns effective-columns)
     :cm360 cm360
     :read-only read-only
     :validation {:show-errors show-errors :set-show-errors set-show-errors}
     :on-save on-save}))

(defn use-csv-actions [{{:keys [rows set-rows]} :row-controls
                        {:keys [columns set-columns]} :column-controls
                        {:keys [set-show-errors]} :validation}]
  (let [[show-warning set-show-warning] (uix/use-state false)
        has-columns (boolean (seq columns))
        do-download (uix/use-callback
                     #(csv/download-csv (->clj rows) columns)
                     [rows columns])
        on-download (uix/use-callback
                     (fn []
                       (if has-columns
                         (do-download)
                         (set-show-warning true)))
                     [has-columns do-download])
        on-file (uix/use-callback
                 (fn [^js file]
                   (let [reader (js/FileReader.)]
                     (set! (.-onload reader)
                           (fn [^js ev]
                             (let [parsed (csv/parse-csv (.. ev -target -result))
                                   imp-cols (cols/impression-columns columns)
                                   new-imp-cols (csv/needed-impression-columns parsed imp-cols)
                                   new-fixed-cols (csv/needed-fixed-columns parsed columns)
                                   new-cols (into new-fixed-cols new-imp-cols)]
                               (when (seq new-cols)
                                 (set-columns #(into % new-cols)))
                               (set-rows #(csv/apply-csv-to-rows % parsed (into imp-cols new-imp-cols)))
                               (set-show-errors true))))
                     (.readAsText reader file)))
                 [columns set-columns set-rows set-show-errors])]
    {:csv {:on-download on-download :on-file on-file}
     :warning {:visible (and show-warning (not has-columns))
               :on-download (fn [] (set-show-warning false) (do-download))
               :on-dismiss #(set-show-warning false)}}))
